import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router, UrlTree } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ThemeConstantService } from "../../services/theme_constant.service";
import { NzModalService } from "ng-zorro-antd/modal";
import { LoginService } from "../../services/login.service";
import { AuthStateService } from "../../services/auth_state.service";
import { NzMessageService } from "ng-zorro-antd/message";
import { fromEvent, merge, Subscription } from "rxjs";
import { auditTime, filter, finalize, map } from "rxjs/operators";
import { SupportRequestsService } from "../../services/support_requests.service";
import { FeedbackContextService } from "../../services/feedback_context.service";
import { ProductLicensesService } from "../../services/product_licenses.service";
import { TutorialModalService } from "../../services/tutorial_modal.service";
import { AssessmentTaskLaunchService } from "../../services/assessment_task_launch.service";
import { AssessmentTaskNotificationReplayService } from "../../services/assessment_task_notification_replay.service";
import { EvidenceRequirementsLaunchService } from "../../services/evidence_requirements_launch.service";
import { AssessmentWorkItemsService } from "../../services/assessment_work_items.service";
import { AssessmentTaskInvalidationService } from "../../services/assessment_task_invalidation.service";
import {
  EvidenceRequirementInvalidation,
  EvidenceRequirementInvalidationService
} from "../../services/evidence_requirement_invalidation.service";
import { assessmentTaskSessionIdentity } from "../../services/assessment_task_session.util";
import {
  OrganizationBrandingAssetType,
  OrganizationBrandingService
} from "../../services/organization_branding.service";
import {
  getDefaultOrganizationBrandingUrl
} from "../../utils/organization_branding_header.util";
import {
  UserNotification,
  UserNotificationsService,
  extractEvidenceReminderTarget
} from "../../services/user_notifications.service";
import { encodeBase64RouteValue } from "../../utils/base64_route_value.util";
import { encodeAssessmentTaskId } from "../../utils/assessment_task_route.util";
import {
  buildSecureStorageEvidenceItemUploadQueryParams,
  buildSecureStorageMerchantQueryParams,
  SECURE_STORAGE_MANAGE_FILES_ROUTE,
  SECURE_STORAGE_UPLOAD_FILES_ROUTE
} from "../../utils/secure_storage_route.util";

const SHOW_NOTIFICATIONS_INTENT = encodeBase64RouteValue("true");

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit, OnDestroy {
  searchVisible: boolean = false;
  quickViewVisible: boolean = false;
  isFolded: boolean;
  isExpand: boolean;
  userDetails: any = {};
  canAccessMerchants: boolean = false;
  canSwitchAccounts: boolean = false;
  isGlobalAdmin: boolean = false;
  isFileUploaderUser: boolean = false;
  homeRoute: string = "/dashboard/home";
  feedbackForm: FormGroup;
  isFeedbackModalVisible: boolean = false;
  isSubmittingFeedback: boolean = false;
  feedbackSubmitSuccess: boolean = false;
  feedbackSubmitError: string | null = null;
  isTutorialModalVisible: boolean = false;
  tutorialStepIndex: number = 0;
  tutorialSlides: string[] = [];
  isAdmin: boolean = false;
  accountLicenseCredits: number = 0;
  isLoadingAccountCredits: boolean = false;
  userNotifications: UserNotification[] = [];
  unreadNotificationCount: number = 0;
  isLoadingNotifications: boolean = false;
  unfinishedWorkCount: number | null = null;
  isLoadingWorkCount: boolean = false;
  workCountError: boolean = false;
  readonly archivingNotificationIds = new Set<number>();
  readonly evidenceReminderTarget = extractEvidenceReminderTarget;
  brandingHorizontalLogoUrl: string = getDefaultOrganizationBrandingUrl("header_horizontal");
  brandingIconLogoUrl: string = getDefaultOrganizationBrandingUrl("header_icon");
  private userSubscription: Subscription | null = null;
  private accountCreditsSubscription: Subscription | null = null;
  private brandingSubscription: Subscription | null = null;
  private brandingRefreshSubscription: Subscription | null = null;
  private brandingAssetSubscriptions: Partial<Record<OrganizationBrandingAssetType, Subscription>> = {};
  private brandingAccountId: number | null = null;
  private routeSubscription: Subscription | null = null;
  private tutorialRequestSubscription: Subscription | null = null;
  private notificationStateSubscription: Subscription | null = null;
  private notificationsSubscription: Subscription | null = null;
  private markNotificationsReadSubscription: Subscription | null = null;
  isNotificationsOverlayVisible: boolean = false;
  private notificationOwnerKey: string | null = null;
  private notificationOwnerToken: string | null = null;
  private consumedNotificationIntentUrl: string | null = null;
  private workCountOwnerKey: string | null = null;
  private workCountSessionToken: string | null = null;
  private workCountRequest: Subscription | null = null;
  private workCountRequestVersion = 0;
  private readonly workCountListeners = new Subscription();
  private readonly archiveNotificationSubscriptions = new Map<number, Subscription>();
  private tutorialTimer: number | null = null;
  private readonly tutorialAutoOpenStoragePrefix = "cb4.tutorialAutoOpened";

  constructor(
    private themeService: ThemeConstantService,
    private modal: NzModalService,
    private loginService: LoginService,
    private authState: AuthStateService,
    private message: NzMessageService,
    private readonly supportRequests: SupportRequestsService,
    private readonly feedbackContext: FeedbackContextService,
    private readonly productLicensesService: ProductLicensesService,
    private readonly tutorialModalService: TutorialModalService,
    private readonly assessmentTaskLaunch: AssessmentTaskLaunchService,
    private readonly taskNotificationReplay: AssessmentTaskNotificationReplayService,
    private readonly evidenceRequirementsLaunch: EvidenceRequirementsLaunchService,
    private readonly workItems: AssessmentWorkItemsService,
    private readonly taskInvalidations: AssessmentTaskInvalidationService,
    private readonly evidenceInvalidations: EvidenceRequirementInvalidationService,
    private readonly organizationBrandingService: OrganizationBrandingService,
    private readonly userNotificationsService: UserNotificationsService,
    private readonly fb: FormBuilder,
    private readonly router: Router
  ) {
    this.feedbackForm = this.fb.group({
      feedback: ["", [Validators.required, Validators.maxLength(5000)]],
    });
  }

  ngOnInit(): void {
    this.themeService.isMenuFoldedChanges.subscribe(
      (isFolded) => (this.isFolded = isFolded)
    );
    this.themeService.isExpandChanges.subscribe(
      (isExpand) => (this.isExpand = isExpand)
    );
    this.notificationStateSubscription = this.userNotificationsService
      .watchNotificationState()
      .subscribe((state) => {
        this.userNotifications = state.items;
        this.unreadNotificationCount = state.unreadCount;
        this.isLoadingNotifications = state.isLoading;
      });
    this.refreshUserState(this.loginService.getUserSnapshot());
    this.userSubscription = this.loginService.getUserUpdates().subscribe((user) => {
      this.refreshUserState(user);
    });
    this.brandingRefreshSubscription = this.organizationBrandingService
      .watchBrandingChanges()
      .subscribe(() => this.loadOrganizationBranding(true));

    this.updateRouteContext();
    this.consumeNotificationLinkIntent();
    this.routeSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateRouteContext();
        this.consumeNotificationLinkIntent(true);
        this.refreshMyUnfinishedCount();
      });

    this.workCountListeners.add(merge(
      this.taskInvalidations.watchInvalidations().pipe(
        filter(({ kind }) => kind === "task" || kind === "delete" || kind === "response" || kind === "scope")
      ),
      this.evidenceInvalidations.watchChanges().pipe(
        filter((change) => this.evidenceChangeAffectsWorkCount(change))
      )
    ).pipe(
      map(() => ({
        ownerKey: this.workCountOwnerKey,
        token: this.workCountSessionToken,
        accountId: this.loginService.getCurrentAccountId()
      })),
      auditTime(0)
    ).subscribe(({ ownerKey, token, accountId }) => {
      if (ownerKey && token && accountId === this.loginService.getCurrentAccountId() &&
          this.isCurrentWorkCountSession(ownerKey, token)) this.refreshMyUnfinishedCount(true);
    }));
    this.workCountListeners.add(this.taskInvalidations.watchInvalidations().pipe(
      filter((event) => event.kind === "scope" && event.source === "mutation"),
      map(() => ({ owner: this.notificationOwnerKey, token: this.notificationOwnerToken })),
      auditTime(0)
    ).subscribe(({ owner, token }) => {
      if (owner && token && owner === this.notificationOwnerKey && token === this.notificationOwnerToken &&
          owner === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot()) && token === this.loginService.getToken()) {
        this.loadNotifications();
      }
    }));
    if (typeof window !== "undefined") {
      this.workCountListeners.add(fromEvent(window, "focus").subscribe(() => this.refreshMyUnfinishedCount()));
    }
    if (typeof document !== "undefined") {
      this.workCountListeners.add(fromEvent(document, "visibilitychange").subscribe(() => {
        if (document.visibilityState === "visible") this.refreshMyUnfinishedCount();
      }));
    }

    this.tutorialRequestSubscription = this.tutorialModalService
      .watchOpenTutorialRequests()
      .subscribe(() => this.openTutorialModal());
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.workCountListeners.unsubscribe();
    this.workCountRequest?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.tutorialRequestSubscription?.unsubscribe();
    this.notificationStateSubscription?.unsubscribe();
    this.notificationsSubscription?.unsubscribe();
    this.markNotificationsReadSubscription?.unsubscribe();
    this.userNotificationsService.stopSummaryPolling();
    this.archiveNotificationSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.archiveNotificationSubscriptions.clear();
    this.clearTutorialTimer();
    this.accountCreditsSubscription?.unsubscribe();
    this.brandingSubscription?.unsubscribe();
    this.brandingRefreshSubscription?.unsubscribe();
    this.clearBrandingAssetRequest("header_horizontal");
    this.clearBrandingAssetRequest("header_icon");
  }

  logout(): void {
    this.modal.confirm({
      nzTitle: 'Log out?',
      nzContent: 'Are you sure you want to log out of your session?',
      nzOkText: 'Log out',
      nzOkType: 'primary',
      nzCentered: true,
      nzOnOk: () => this.performLogout(),
      nzCancelText: 'Cancel'
    });
  }

  private performLogout(): void {
    this.loginService.logout('BUTTON').subscribe({
      next: () => {
        this.authState.clearAllRedirects();
      },
      error: () => {
        this.authState.clearAllRedirects();
      }
    });
  }

  toggleFold() {
    this.isFolded = !this.isFolded;
    this.themeService.toggleFold(this.isFolded);
  }

  toggleExpand() {
    const width = window.innerWidth;
    const shouldForceCompact = width < 992;
    const shouldFoldByWidth = width < 1700;

    this.isExpand = !this.isExpand;
    this.themeService.toggleExpand(this.isExpand);

    this.isFolded = shouldForceCompact ? true : shouldFoldByWidth;
    this.themeService.toggleFold(this.isFolded);
  }

  searchToggle(): void {
    this.searchVisible = !this.searchVisible;
  }

  quickViewToggle(): void {
    this.quickViewVisible = !this.quickViewVisible;
  }

  openFeedbackModal(): void {
    this.feedbackSubmitError = null;
    this.feedbackSubmitSuccess = false;
    this.isFeedbackModalVisible = true;
  }

  onNotificationsVisibleChange(isVisible: boolean): void {
    if (isVisible && !this.notificationOwnerKey) return;
    if (this.isNotificationsOverlayVisible === isVisible) {
      return;
    }
    this.isNotificationsOverlayVisible = isVisible;
    if (!isVisible) {
      return;
    }

    this.loadNotifications(true);
  }

  private consumeNotificationLinkIntent(afterNavigationEnd: boolean = false): void {
    if (!this.notificationOwnerKey || !this.loginService.isLoggedIn()) return;
    if (!afterNavigationEnd && (!this.router.navigated || this.router.getCurrentNavigation())) return;

    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const values = urlTree.queryParamMap.getAll("showNotifications");
    if (values.length === 0) {
      this.consumedNotificationIntentUrl = null;
      return;
    }
    if (this.consumedNotificationIntentUrl === currentUrl) return;
    this.consumedNotificationIntentUrl = currentUrl;

    if (values.length === 1 && values[0] === SHOW_NOTIFICATIONS_INTENT) {
      this.onNotificationsVisibleChange(true);
    }

    delete urlTree.queryParams["showNotifications"];
    void this.router.navigateByUrl(urlTree, { replaceUrl: true }).catch(() => undefined);
  }

  archiveNotification(notification: UserNotification): void {
    if (!notification || this.archivingNotificationIds.has(notification.id)) {
      return;
    }

    this.archivingNotificationIds.add(notification.id);
    const subscription = this.userNotificationsService
      .archiveNotification(notification.id)
      .pipe(finalize(() => {
        this.archivingNotificationIds.delete(notification.id);
        this.archiveNotificationSubscriptions.delete(notification.id);
      }))
      .subscribe({
        error: (error: Error) => {
          this.message.error(error?.message || "Unable to delete this notification.");
        }
      });
    this.archiveNotificationSubscriptions.set(notification.id, subscription);
  }

  notificationLink(notification: UserNotification): UrlTree | null {
    const link = notification.link;
    if (!link) {
      return null;
    }
    if (link.routeType === "assessment" && link.productLicenseId && link.formKey) {
      return this.router.createUrlTree(
        [link.formKey === "roc_form" ? "/roc_form" : "/saq_form"],
        {
          queryParams: {
            productLicenseId: encodeBase64RouteValue(String(link.productLicenseId)),
            activeSection: link.activeSection ? encodeBase64RouteValue(link.activeSection) : null,
            activeRequirement: link.activeRequirement ? encodeBase64RouteValue(link.activeRequirement) : null,
            taskId: link.taskId ? encodeAssessmentTaskId(link.taskId) : null
          }
        }
      );
    }
    if (link.routeType === "evidence_requirement_upload" &&
        link.merchantId && link.productLicenseId && link.evidenceRequirementId) {
      return this.router.createUrlTree([...SECURE_STORAGE_UPLOAD_FILES_ROUTE], {
        queryParams: buildSecureStorageEvidenceItemUploadQueryParams(
          link.merchantId,
          link.productLicenseId,
          link.evidenceRequirementId
        )
      });
    }
    if (link.routeType === "secure_storage_manage_files" && link.merchantId) {
      return this.router.createUrlTree([...SECURE_STORAGE_MANAGE_FILES_ROUTE], {
        queryParams: buildSecureStorageMerchantQueryParams(link.merchantId)
      });
    }
    if (link.routeType === "secure_storage_dashboard") {
      return this.router.createUrlTree(["/secure-storage/dashboard"]);
    }
    return link.routeType === "user_settings" ? this.router.createUrlTree(["/user-settings"]) : null;
  }

  onNotificationLinkClick(notification: UserNotification, link: UrlTree): void {
    const taskId = notification.link?.taskId;
    if (taskId && this.router.isActive(link, {
      paths: "exact",
      queryParams: "subset",
      fragment: "ignored",
      matrixParams: "ignored"
    })) {
      this.taskNotificationReplay.request(taskId);
    }
  }

  openEvidenceReminder(notification: UserNotification): void {
    const target = extractEvidenceReminderTarget(notification.link);
    if (!target || !this.userNotifications.includes(notification)) return;
    if (!this.loginService.isLoggedIn() ||
        !this.notificationOwnerKey ||
        this.notificationOwnerKey !== assessmentTaskSessionIdentity(this.loginService.getUserSnapshot()) ||
        !this.notificationOwnerToken || this.notificationOwnerToken !== this.loginService.getToken()) {
      // A token can change before the user update reaches this component.
      this.configureNotificationRefresh();
      return;
    }

    this.onNotificationsVisibleChange(false);
    this.evidenceRequirementsLaunch.openItem(
      target.productLicenseId, target.evidenceRequirementId, target.merchantId
    );
  }

  openTutorialModal(): void {
    if (this.isFileUploaderUser) {
      return;
    }

    this.clearTutorialTimer();
    this.tutorialStepIndex = 0;
    this.isTutorialModalVisible = true;
  }

  closeTutorialModal(): void {
    this.isTutorialModalVisible = false;
  }

  advanceTutorial(): void {
    if (this.tutorialStepIndex >= this.tutorialSlides.length - 1) {
      this.closeTutorialModal();
      return;
    }

    this.tutorialStepIndex += 1;
  }

  handleFeedbackCancel(): void {
    this.isFeedbackModalVisible = false;
    this.feedbackForm.reset();
    this.feedbackSubmitError = null;
    this.feedbackSubmitSuccess = false;
  }

  submitFeedback(): void {
    this.feedbackSubmitError = null;
    this.feedbackSubmitSuccess = false;
    this.markFeedbackControlsAsDirty();

    if (this.feedbackForm.invalid) {
      return;
    }

    this.isSubmittingFeedback = true;

    const userMessage = this.feedbackControl?.value ?? "";
    const issueDescription = this.feedbackContext.composeIssueDescription(userMessage);
    const topic = this.buildFeedbackTopic();

    this.supportRequests
      .createSupportRequest({
        topic,
        issueDescription,
        userIssueDescription: userMessage,
        requestType: "feedback"
      })
      .pipe(finalize(() => (this.isSubmittingFeedback = false)))
      .subscribe({
        next: () => {
          this.feedbackSubmitSuccess = true;
          this.message.success("Thanks for sharing your feedback. We'll follow up within 24 hours.");
          this.feedbackForm.reset();
        },
        error: (error: Error) => {
          this.feedbackSubmitError = error?.message || "We were unable to submit your feedback. Please try again.";
          this.message.error(this.feedbackSubmitError);
        },
      });
  }

  private refreshUserState(user: any): void {
    this.userDetails = user || {};
    this.isFileUploaderUser = this.loginService.isFileUploader(this.userDetails);
    this.configureWorkCountRefresh(user);
    this.homeRoute = this.loginService.getDefaultAuthenticatedRoute(this.userDetails);
    this.canAccessMerchants = this.loginService.hasAdminPrivileges();
    this.canSwitchAccounts = this.loginService.canSwitchAccounts();
    this.isGlobalAdmin = this.loginService.isGlobalAdmin();
    this.isAdmin = this.loginService.hasAdminPrivileges();
    this.loadOrganizationBranding();
    this.loadAccountLicenseCredits();
    this.configureNotificationRefresh();
    this.consumeNotificationLinkIntent();
    this.buildTutorialSlides();
    this.scheduleTutorialAutoOpen();
  }

  private loadAccountLicenseCredits(): void {
    this.accountCreditsSubscription?.unsubscribe();

    if (!this.isAdmin) {
      this.accountLicenseCredits = 0;
      this.isLoadingAccountCredits = false;
      return;
    }

    this.isLoadingAccountCredits = true;
    this.accountCreditsSubscription = this.productLicensesService
      .watchAccountLicenseCredits()
      .pipe(
        finalize(() => {
          this.isLoadingAccountCredits = false;
        })
      )
      .subscribe({
        next: (result) => {
          this.accountLicenseCredits = Number(result?.licenseCredits) || 0;
        },
        error: () => {
          this.accountLicenseCredits = 0;
        },
      });
  }

  private configureNotificationRefresh(): void {
    const ownerKey = this.loginService.isLoggedIn()
      ? assessmentTaskSessionIdentity(this.loginService.getUserSnapshot())
      : null;
    const ownerToken = ownerKey ? this.loginService.getToken() : null;
    if (ownerKey === this.notificationOwnerKey && ownerToken === this.notificationOwnerToken) {
      return;
    }

    this.notificationOwnerKey = ownerKey;
    this.notificationOwnerToken = ownerToken;
    this.isNotificationsOverlayVisible = false;
    this.notificationsSubscription?.unsubscribe();
    this.markNotificationsReadSubscription?.unsubscribe();
    this.userNotificationsService.clearNotificationState();
    if (ownerKey !== null) {
      this.loadNotifications();
      this.userNotificationsService.startSummaryPolling();
    }
  }

  private loadNotifications(markUnreadAsRead: boolean = false): void {
    this.notificationsSubscription?.unsubscribe();
    this.markNotificationsReadSubscription?.unsubscribe();

    this.notificationsSubscription = this.userNotificationsService
      .refreshCurrentUserNotifications()
      .subscribe({
        next: ({ unreadCount }) => {
          if (markUnreadAsRead && this.isNotificationsOverlayVisible && unreadCount > 0) {
            this.markLoadedNotificationsRead();
          }
        },
        error: () => undefined
      });
  }

  private markLoadedNotificationsRead(): void {
    this.markNotificationsReadSubscription = this.userNotificationsService
      .markCurrentUserNotificationsRead()
      .subscribe({
        next: () => undefined,
        error: () => {
          // Retain the fetched unread state so a later overlay open can retry the transition.
        }
      });
  }

  openOrganizationSettings(): void {
    if (!this.isAdmin) {
      return;
    }

    void this.router.navigate(["/organization-settings"]);
  }

  openMyTasks(): void {
    if (!this.canOpenTasks || !this.loginService.isLoggedIn()) return;
    this.assessmentTaskLaunch.openHeader();
  }

  get canOpenTasks(): boolean {
    return !!this.workCountOwnerKey && !!this.workCountSessionToken &&
      this.isCurrentWorkCountSession(this.workCountOwnerKey, this.workCountSessionToken);
  }

  get taskButtonLabel(): string {
    if (this.workCountError) return "Open my tasks; unfinished work count unavailable";
    if (this.unfinishedWorkCount !== null) {
      return `Open my tasks; ${this.unfinishedWorkCount} unfinished work items`;
    }
    return "Open my tasks; unfinished work count loading";
  }

  private configureWorkCountRefresh(user: unknown): void {
    const token = this.loginService.getToken();
    const ownerKey = this.loginService.isLoggedIn() && token
      ? assessmentTaskSessionIdentity(user) : null;
    const sessionToken = ownerKey ? token : null;
    if (ownerKey === this.workCountOwnerKey && sessionToken === this.workCountSessionToken) return;

    this.workCountOwnerKey = ownerKey;
    this.workCountSessionToken = sessionToken;
    this.workCountRequestVersion += 1;
    this.workCountRequest?.unsubscribe();
    this.workCountRequest = null;
    this.unfinishedWorkCount = null;
    this.isLoadingWorkCount = false;
    this.workCountError = false;
    if (ownerKey) this.refreshMyUnfinishedCount();
  }

  private evidenceChangeAffectsWorkCount(change: EvidenceRequirementInvalidation): boolean {
    if (!this.canOpenTasks || !change.unfinishedCountMayHaveChanged ||
        change.accountId !== this.loginService.getCurrentAccountId()) return false;
    if (change.items === null) return true;

    const userId = this.loginService.getCurrentUserId();
    if (!userId) return false;
    return change.items.some(({ before, after }) => {
      // A missing side is expected for create/delete, but is unknown for other writes.
      const scopes = change.kind === "create" ? [after]
        : change.kind === "delete" ? [before] : [before, after];
      return scopes.some((scope) => scope === null || scope.status === null ||
        (scope.status !== "submitted_reviewed" &&
          (scope.assignedUserIds === null || scope.assignedUserIds.includes(userId))));
    });
  }

  private refreshMyUnfinishedCount(force = false): void {
    const ownerKey = this.workCountOwnerKey;
    const token = this.workCountSessionToken;
    if (!ownerKey || !token || !this.isCurrentWorkCountSession(ownerKey, token) ||
        (typeof document !== "undefined" && document.visibilityState === "hidden") ||
        (this.isLoadingWorkCount && !force)) return;

    this.workCountRequest?.unsubscribe();
    const version = ++this.workCountRequestVersion;
    this.isLoadingWorkCount = true;
    this.workCountError = false;
    this.workCountRequest = this.workItems.getMyUnfinishedCount().subscribe({
      next: ({ count }) => {
        if (version !== this.workCountRequestVersion || !this.isCurrentWorkCountSession(ownerKey, token)) return;
        this.unfinishedWorkCount = count;
        this.isLoadingWorkCount = false;
      },
      error: () => {
        if (version !== this.workCountRequestVersion || !this.isCurrentWorkCountSession(ownerKey, token)) return;
        this.unfinishedWorkCount = null;
        this.isLoadingWorkCount = false;
        this.workCountError = true;
        this.workCountRequest = null;
      },
      complete: () => {
        if (version === this.workCountRequestVersion) this.workCountRequest = null;
      }
    });
  }

  private isCurrentWorkCountSession(ownerKey: string, token: string): boolean {
    return ownerKey === this.workCountOwnerKey && token === this.workCountSessionToken &&
      this.loginService.isLoggedIn() && token === this.loginService.getToken() &&
      ownerKey === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
  }

  private buildTutorialSlides(): void {
    const slides = ["welcome", "assessments", "dashboard"];

    if (this.isGlobalAdmin) {
      slides.push("global-admin");
    }

    slides.push("features", "support");
    this.tutorialSlides = slides;

    if (this.tutorialStepIndex >= slides.length) {
      this.tutorialStepIndex = 0;
    }
  }

  private scheduleTutorialAutoOpen(): void {
    if (typeof window === "undefined" || this.tutorialTimer || this.isTutorialModalVisible) {
      return;
    }

    if (!this.shouldAutoOpenTutorial()) {
      return;
    }

    this.tutorialTimer = window.setTimeout(() => {
      this.tutorialTimer = null;
      if (!this.shouldAutoOpenTutorial()) {
        return;
      }

      this.markTutorialAutoOpened();
      this.openTutorialModal();
    }, 1000);
  }

  private clearTutorialTimer(): void {
    if (this.tutorialTimer) {
      window.clearTimeout(this.tutorialTimer);
      this.tutorialTimer = null;
    }
  }

  private shouldAutoOpenTutorial(): boolean {
    if (!this.loginService.isLoggedIn()) {
      return false;
    }

    if (this.isFileUploaderUser) {
      return false;
    }

    const isFirstLogin = this.userDetails?.is_first_login ?? this.userDetails?.isFirstLogin;
    if (isFirstLogin !== true) {
      return false;
    }

    return !this.hasTutorialAutoOpened();
  }

  private hasTutorialAutoOpened(): boolean {
    if (typeof window === "undefined") {
      return false;
    }

    const storageKey = this.getTutorialAutoOpenStorageKey();
    if (!storageKey) {
      return false;
    }

    return window.localStorage.getItem(storageKey) === "1";
  }

  private markTutorialAutoOpened(): void {
    if (typeof window === "undefined") {
      return;
    }

    const storageKey = this.getTutorialAutoOpenStorageKey();
    if (!storageKey) {
      return;
    }

    window.localStorage.setItem(storageKey, "1");
  }

  private getTutorialAutoOpenStorageKey(): string | null {
    const userIdentifier = this.userDetails?.user_id ?? this.userDetails?.userId ?? this.userDetails?.email;
    if (!userIdentifier) {
      return null;
    }

    return `${this.tutorialAutoOpenStoragePrefix}.${String(userIdentifier)}`;
  }

  private markFeedbackControlsAsDirty(): void {
    Object.values(this.feedbackForm.controls).forEach((control) => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
  }

  private buildFeedbackTopic(): string {
    const snapshot = this.feedbackContext.getSnapshot();
    const baseTitle = snapshot.routeTitle || "User Feedback";
    const normalizedTitle = baseTitle.replace(/\s+/g, " ").trim() || "User Feedback";
    const prefix = "Feedback - ";
    const maxLength = 255;
    const availableLength = maxLength - prefix.length;
    const trimmedTitle =
      normalizedTitle.length > availableLength ? normalizedTitle.slice(0, availableLength) : normalizedTitle;

    return `${prefix}${trimmedTitle}`;
  }

  private updateRouteContext(): void {
    const routeTitle = this.resolveRouteTitle(this.router.routerState.snapshot.root);
    this.feedbackContext.updateRouteContext(this.router.url, routeTitle);
  }

  private resolveRouteTitle(route: ActivatedRouteSnapshot | null): string | null {
    if (!route) {
      return null;
    }

    let current: ActivatedRouteSnapshot | null = route;
    while (current.firstChild) {
      current = current.firstChild;
    }

    const title = current.data?.["title"];
    return typeof title === "string" && title.trim() ? title.trim() : null;
  }

  get feedbackControl() {
    return this.feedbackForm.get("feedback");
  }

  get currentTutorialSlide(): string {
    return this.tutorialSlides[this.tutorialStepIndex] || "welcome";
  }

  get isLastTutorialStep(): boolean {
    return this.tutorialStepIndex >= this.tutorialSlides.length - 1;
  }

  get tutorialStepLabel(): string {
    return `${this.tutorialStepIndex + 1} of ${this.tutorialSlides.length}`;
  }

  get userSettingsRoute(): string {
    return "/user-settings";
  }

  handleBrandingImageError(assetType: OrganizationBrandingAssetType): void {
    this.revokeBrandingObjectUrl(assetType);
    if (assetType === "header_horizontal") {
      this.brandingHorizontalLogoUrl = getDefaultOrganizationBrandingUrl(assetType);
      return;
    }

    this.brandingIconLogoUrl = getDefaultOrganizationBrandingUrl(assetType);
  }

  private loadOrganizationBranding(forceReload: boolean = false): void {
    const accountId = this.loginService.getCurrentAccountId();
    if (!forceReload && accountId === this.brandingAccountId && this.brandingSubscription) {
      return;
    }

    this.brandingSubscription?.unsubscribe();
    this.brandingAccountId = accountId;
    this.resetBrandingAsset("header_horizontal");
    this.resetBrandingAsset("header_icon");

    if (!accountId) {
      this.brandingSubscription = null;
      return;
    }

    this.brandingSubscription = this.organizationBrandingService.getActiveAssets().subscribe({
      next: (assets) => {
        for (const assetType of ["header_horizontal", "header_icon"] as OrganizationBrandingAssetType[]) {
          if (assets.some((asset) => asset.assetType === assetType)) {
            this.loadBrandingAssetBlob(assetType, accountId);
          }
        }
      },
      error: () => {
        this.handleBrandingImageError("header_horizontal");
        this.handleBrandingImageError("header_icon");
      }
    });
  }

  private loadBrandingAssetBlob(assetType: OrganizationBrandingAssetType, accountId: number): void {
    this.clearBrandingAssetRequest(assetType);
    this.brandingAssetSubscriptions[assetType] = this.organizationBrandingService
      .fetchAssetBlob(assetType)
      .subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          if (this.brandingAccountId !== accountId) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          this.revokeBrandingObjectUrl(assetType);
          if (assetType === "header_horizontal") {
            this.brandingHorizontalLogoUrl = objectUrl;
            return;
          }

          this.brandingIconLogoUrl = objectUrl;
        },
        error: () => this.handleBrandingImageError(assetType)
      });
  }

  private resetBrandingAsset(assetType: OrganizationBrandingAssetType): void {
    this.clearBrandingAssetRequest(assetType);
    this.revokeBrandingObjectUrl(assetType);
    if (assetType === "header_horizontal") {
      this.brandingHorizontalLogoUrl = getDefaultOrganizationBrandingUrl(assetType);
      return;
    }

    this.brandingIconLogoUrl = getDefaultOrganizationBrandingUrl(assetType);
  }

  private clearBrandingAssetRequest(assetType: OrganizationBrandingAssetType): void {
    this.brandingAssetSubscriptions[assetType]?.unsubscribe();
    delete this.brandingAssetSubscriptions[assetType];
  }

  private revokeBrandingObjectUrl(assetType: OrganizationBrandingAssetType): void {
    const url = assetType === "header_horizontal" ? this.brandingHorizontalLogoUrl : this.brandingIconLogoUrl;
    if (url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }

  notificationList = [];
  /*
  notificationList = [
    {
      title: "You received a new message",
      time: "8 min",
      icon: "mail",
      color: "ant-avatar-" + "blue",
    },
    {
      title: "New user registered",
      time: "7 hours",
      icon: "user-add",
      color: "ant-avatar-" + "cyan",
    },
    {
      title: "System Alert",
      time: "8 hours",
      icon: "warning",
      color: "ant-avatar-" + "red",
    },
    {
      title: "You have a new update",
      time: "2 days",
      icon: "sync",
      color: "ant-avatar-" + "gold",
    },
  ];
  */
}
